import React, { useState, useEffect } from 'react';
import { FaTimes, FaSpinner, FaUser, FaEnvelope, FaWhatsapp, FaSave, FaRedo, FaCalendarAlt } from 'react-icons/fa';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Button from '../../../components/Button';
import HistorialAcciones from './HistorialAcciones';
import { adminUserService } from '../../../services/adminUserService';
import { toastSuccess, toastError } from '../../../utils/toast';
import { swalConfirm } from '../../../utils/swal';
import { useAuth } from '../../../context/AuthContext';

const TABS = ['Perfil', 'Límites', 'Bloqueo', 'Historial'];

const ProgressBar = ({ used, limit, label }) => {
  const safeLimit = limit > 0 ? limit : 1;
  const pct = Math.min(100, Math.max(0, Math.round((used / safeLimit) * 100)));
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-green-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs" style={{ color: '#9BA3B8' }}>
        <span>{label}</span>
        <span>{(used ?? 0).toLocaleString()} / {(limit ?? 0).toLocaleString()} por semana</span>
      </div>
      <div className="w-full rounded-full h-2" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
        <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const UsageChart = ({ userId }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminUserService.getUsageChart(userId)
      .then(setChartData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center py-6" style={{ color: '#6B7391' }}>
        <FaSpinner className="animate-spin" size={20} />
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8" style={{ color: '#6B7391' }}>
        <FaCalendarAlt size={28} className="mb-2 opacity-40" />
        <p className="text-sm">Sin uso esta semana</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
        <XAxis dataKey="date_label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 8 }}
          formatter={(val, name) => [val.toLocaleString(), name === 'text_count' ? 'Texto' : 'Voz']}
        />
        <Legend
          iconSize={10}
          formatter={(name) => name === 'text_count' ? 'Texto' : 'Voz'}
          wrapperStyle={{ fontSize: 11 }}
        />
        <Bar dataKey="text_count" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={16} />
        <Bar dataKey="voice_count" fill="#22c55e" radius={[3, 3, 0, 0]} maxBarSize={16} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const PanelDetalleUsuario = ({ user, plans, onClose, onUpdated }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('Perfil');
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  // Perfil fields
  const [perfil, setPerfil] = useState({ full_name: '', whatsapp_number: '' });

  // Límites fields
  const [planId, setPlanId] = useState('');
  const [useOverride, setUseOverride] = useState(false);
  const [customText, setCustomText] = useState('');
  const [customVoice, setCustomVoice] = useState('');

  // Bloqueo fields
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const planOptions = (plans || []).map(p => ({ value: p.id, label: p.name }));

  useEffect(() => {
    if (!user) return;
    if (user._goToTab) setActiveTab(user._goToTab);
    setPerfil({
      full_name: user.full_name || '',
      whatsapp_number: user.whatsapp_number || '',
    });
    setIsBlocked(!!user.is_blocked);
    setBlockReason(user.block_reason || '');

    setLoadingSettings(true);
    adminUserService.getUserSettings(user.user_id)
      .then(s => {
        setSettings(s);
        if (s) {
          setPlanId(s.plan_id || '');
          const hasOverride = s.custom_text_limit_weekly !== null || s.custom_voice_limit_weekly !== null;
          setUseOverride(hasOverride);
          setCustomText(s.custom_text_limit_weekly !== null ? String(s.custom_text_limit_weekly) : '');
          setCustomVoice(s.custom_voice_limit_weekly !== null ? String(s.custom_voice_limit_weekly) : '');
        }
      })
      .catch(console.error)
      .finally(() => setLoadingSettings(false));
  }, [user]);

  const handleSavePerfil = async () => {
    setSaving(true);
    try {
      await adminUserService.updateProfile(user.user_id, perfil);
      toastSuccess('Perfil actualizado');
      onUpdated();
    } catch (err) {
      toastError(err.message || 'Error al actualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLimites = async () => {
    setSaving(true);
    try {
      if (planId !== settings?.plan_id) {
        await adminUserService.changePlan({
          userId: user.user_id,
          planId,
          adminId: currentUser?.id,
          beforePlanId: settings?.plan_id,
        });
      }

      const newCustomText = useOverride && customText !== '' ? parseInt(customText, 10) : null;
      const newCustomVoice = useOverride && customVoice !== '' ? parseInt(customVoice, 10) : null;
      const prevCustomText = settings?.custom_text_limit_weekly ?? null;
      const prevCustomVoice = settings?.custom_voice_limit_weekly ?? null;

      if (newCustomText !== prevCustomText || newCustomVoice !== prevCustomVoice) {
        await adminUserService.setCustomLimits({
          userId: user.user_id,
          customTextLimit: newCustomText,
          customVoiceLimit: newCustomVoice,
          adminId: currentUser?.id,
          before: {
            custom_text_limit_weekly: prevCustomText,
            custom_voice_limit_weekly: prevCustomVoice,
          },
        });
      }

      toastSuccess('Límites actualizados');
      onUpdated();
    } catch (err) {
      toastError(err.message || 'Error al actualizar límites');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBloqueo = async () => {
    if (isBlocked && !blockReason.trim()) {
      toastError('Ingresa una razón para bloquear al usuario');
      return;
    }
    setSaving(true);
    try {
      await adminUserService.setBlockStatus({
        userId: user.user_id,
        isBlocked,
        blockReason,
        adminId: currentUser?.id,
        before: { is_blocked: user.is_blocked, block_reason: user.block_reason },
      });
      toastSuccess(isBlocked ? 'Usuario bloqueado' : 'Usuario desbloqueado');
      onUpdated();
    } catch (err) {
      toastError(err.message || 'Error al cambiar estado de bloqueo');
    } finally {
      setSaving(false);
    }
  };

  const handleResetUsage = async () => {
    const { isConfirmed } = await swalConfirm(
      '¿Resetear uso de la semana?',
      'Se pondrán a 0 los contadores de texto y voz desde el lunes hasta hoy.',
      'Sí, resetear',
      { input: null, html: null, footer: null, showDenyButton: false }
    );
    if (!isConfirmed) return;
    setSaving(true);
    try {
      await adminUserService.resetUsage({ userId: user.user_id, adminId: currentUser?.id });
      toastSuccess('Uso reseteado');
      onUpdated();
    } catch (err) {
      toastError(err.message || 'Error al resetear uso');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const fullName = user.full_name || user.email;
  const daysLeft = user.days_remaining_in_week;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black bg-opacity-30" onClick={onClose} />
      <div className="w-full max-w-lg flex flex-col h-full overflow-hidden" style={{ backgroundColor: '#12132a', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', backgroundColor: '#0d0e1e' }}>
          <div>
            <h2 className="font-semibold" style={{ color: '#E8EAF0' }}>{fullName}</h2>
            <p className="text-sm" style={{ color: '#6B7391' }}>{user.email}</p>
          </div>
          <button onClick={onClose} style={{ color: '#4A5270' }} className="hover:text-white transition-colors p-1">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', backgroundColor: '#0d0e1e' }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="flex-1 py-3 text-sm font-medium transition-colors"
              style={{
                color: activeTab === tab ? '#FFFFFF' : '#6B7391',
                borderBottom: activeTab === tab ? '2px solid #DC1F26' : '2px solid transparent',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* ── Perfil ───────────────────────────────────── */}
          {activeTab === 'Perfil' && (
            <div className="space-y-1">
              <Input
                label="Nombre completo"
                value={perfil.full_name}
                onChange={e => setPerfil(p => ({ ...p, full_name: e.target.value }))}
                leftIcon={<FaUser />}
              />
              <Input
                label="Email"
                value={user.email}
                leftIcon={<FaEnvelope />}
                disabled
                className="opacity-60"
                onChange={() => {}}
              />
              <p className="text-xs -mt-4 mb-2 pl-1" style={{ color: '#6B7391' }}>
                Email gestionado desde Auth. Usa la EF update-client-user para cambiarlo.
              </p>
              <Input
                label="WhatsApp"
                value={perfil.whatsapp_number}
                onChange={e => setPerfil(p => ({ ...p, whatsapp_number: e.target.value }))}
                leftIcon={<FaWhatsapp />}
              />
              <div className="pt-2">
                <Button variant="primary" onClick={handleSavePerfil} disabled={saving}>
                  {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
                  Guardar perfil
                </Button>
              </div>
            </div>
          )}

          {/* ── Límites ───────────────────────────────────── */}
          {activeTab === 'Límites' && (
            <div className="space-y-5">
              {loadingSettings ? (
                <div className="flex justify-center py-8" style={{ color: '#6B7391' }}><FaSpinner className="animate-spin" size={24} /></div>
              ) : (
                <>
                  {/* Plan */}
                  <Select
                    label="Plan asignado"
                    options={planOptions}
                    value={planId}
                    onChange={e => setPlanId(e.target.value)}
                    isClearable={false}
                  />

                  {/* Toggle override */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setUseOverride(v => !v)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${useOverride ? 'bg-blue-600' : ''}`}
                      style={!useOverride ? { backgroundColor: 'rgba(255,255,255,0.12)' } : {}}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${useOverride ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-sm font-medium" style={{ color: '#C8CDD9' }}>Override manual de límites</span>
                  </div>

                  {useOverride ? (
                    <div className="grid grid-cols-2 gap-4 p-4 rounded-lg" style={{ backgroundColor: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)' }}>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#C8CDD9' }}>Límite texto/semana</label>
                        <input
                          type="number"
                          min="0"
                          value={customText}
                          onChange={e => setCustomText(e.target.value)}
                          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                          style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)', color: '#E8EAF0' }}
                          placeholder="Ej: 750 peticiones/semana"
                        />
                        <p className="text-xs mt-1" style={{ color: '#6B7391' }}>Si está vacío, se usa el límite del plan</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: '#C8CDD9' }}>Límite voz/semana</label>
                        <input
                          type="number"
                          min="0"
                          value={customVoice}
                          onChange={e => setCustomVoice(e.target.value)}
                          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                          style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)', color: '#E8EAF0' }}
                          placeholder="Ej: 125 peticiones/semana"
                        />
                        <p className="text-xs mt-1" style={{ color: '#6B7391' }}>Si está vacío, se usa el límite del plan</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: '#9BA3B8' }}>
                      Usando límites del plan:{' '}
                      <strong>{settings?.plan?.text_limit_weekly?.toLocaleString() ?? '—'}</strong> texto /{' '}
                      <strong>{settings?.plan?.voice_limit_weekly?.toLocaleString() ?? '—'}</strong> voz por semana
                    </div>
                  )}

                  {/* Uso este mes + días restantes */}
                  <div className="p-4 rounded-lg space-y-3" style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium" style={{ color: '#C8CDD9' }}>Uso esta semana</p>
                      {daysLeft != null && (
                        <span className="text-xs flex items-center gap-1" style={{ color: '#4A5270' }}>
                          <FaCalendarAlt size={11} />
                          {daysLeft} {daysLeft === 1 ? 'día restante' : 'días restantes'}
                        </span>
                      )}
                    </div>
                    <ProgressBar used={Math.max(0, user.text_used ?? 0)} limit={user.text_limit ?? 0} label="Texto" />
                    <ProgressBar used={Math.max(0, user.voice_used ?? 0)} limit={user.voice_limit ?? 0} label="Voz" />
                  </div>

                  {/* Gráfica de uso mensual */}
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <p className="text-sm font-medium mb-3" style={{ color: '#C8CDD9' }}>Uso diario (últimas 4 semanas)</p>
                    <UsageChart userId={user.user_id} />
                  </div>

                  <div className="flex gap-3">
                    <Button variant="primary" onClick={handleSaveLimites} disabled={saving}>
                      {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
                      Guardar límites
                    </Button>
                    <Button variant="outline" onClick={handleResetUsage} disabled={saving}>
                      <FaRedo className="mr-2" />
                      Resetear uso de la semana
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Bloqueo ───────────────────────────────────── */}
          {activeTab === 'Bloqueo' && (
            <div className="space-y-5">
              <div
                className="p-4 rounded-xl border-2 transition-colors"
                style={isBlocked
                  ? { borderColor: 'rgba(239,68,68,0.35)', backgroundColor: 'rgba(239,68,68,0.06)' }
                  : { borderColor: 'rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.04)' }
                }
              >
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setIsBlocked(v => !v)}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${isBlocked ? 'bg-red-500' : ''}`}
                    style={!isBlocked ? { backgroundColor: 'rgba(255,255,255,0.12)' } : {}}
                  >
                    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${isBlocked ? 'translate-x-8' : 'translate-x-1'}`} />
                  </button>
                  <div>
                    <p className="font-semibold" style={{ color: isBlocked ? '#F87171' : '#C8CDD9' }}>
                      {isBlocked ? 'Usuario bloqueado' : 'Usuario activo'}
                    </p>
                    <p className="text-xs" style={{ color: '#6B7391' }}>
                      {isBlocked ? 'Todas las peticiones serán rechazadas' : 'El usuario puede hacer peticiones normalmente'}
                    </p>
                  </div>
                </div>
              </div>

              {isBlocked && (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#C8CDD9' }}>
                    Razón del bloqueo <span style={{ color: '#DC1F26' }}>*</span>
                  </label>
                  <textarea
                    value={blockReason}
                    onChange={e => setBlockReason(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 resize-none"
                    style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)', color: '#E8EAF0' }}
                    placeholder="Describe la razón del bloqueo (visible solo para admins)..."
                  />
                </div>
              )}

              <Button
                variant={isBlocked ? 'danger' : 'success'}
                onClick={handleSaveBloqueo}
                disabled={saving}
                fullWidth
              >
                {saving ? <FaSpinner className="animate-spin mr-2" /> : null}
                {isBlocked ? 'Confirmar bloqueo' : 'Confirmar desbloqueo'}
              </Button>
            </div>
          )}

          {/* ── Historial ───────────────────────────────────── */}
          {activeTab === 'Historial' && (
            <HistorialAcciones userId={user.user_id} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PanelDetalleUsuario;
